import { useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, Loader2, Search, Users, MapPin, CheckCircle2, PieChart, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import toast from "react-hot-toast";
import { useAdminConferenceRooms } from "@/services/adminConferenceRoomService";
import { useParticipantsForPlotting, useAssignRoom, useUnassignRoom } from "@/services/adminConferenceAttendanceService";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export default function RoomParticipantsPage() {
    const { roomId } = useParams<{ roomId: string }>();
    const [searchParams] = useSearchParams();
    const conferenceIdParam = searchParams.get("conference_id");
    const currentRoomId = roomId ? parseInt(roomId) : undefined;

    const [filterSearch, setFilterSearch] = useState("");
    const [filterRole, setFilterRole] = useState<string>("all");
    const [filterPlotted, setFilterPlotted] = useState<string>("all");
    const [filterPayment, setFilterPayment] = useState<string>("all");
    const [filterConferenceId, setFilterConferenceId] = useState<string>(conferenceIdParam || "all");
    
    const [page, setPage] = useState(1);
    const perPage = 20;

    // Fetch Conferences for dropdown
    const { data: conferences, isLoading: isConferencesLoading } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        }
    });

    const { data: rooms } = useAdminConferenceRooms();
    const currentRoom = rooms?.find(r => r.id === currentRoomId);

    const activeConferenceId = filterConferenceId !== "all" ? parseInt(filterConferenceId) : (conferenceIdParam ? parseInt(conferenceIdParam) : undefined);
    const { data: participants, isLoading } = useParticipantsForPlotting(activeConferenceId);
    
    const assignRoom = useAssignRoom();
    const unassignRoom = useUnassignRoom();

    const PAID_STATUSES = ["paid", "sukses"];

    const handleAssign = async (registrationId: number, status: string | null) => {
        if (!activeConferenceId || !currentRoomId) return;
        if (!status || !PAID_STATUSES.includes(status)) {
            const confirmed = window.confirm(
                `Peringatan: registrasi ini berstatus pembayaran "${status || "tidak diketahui"}" (belum lunas). Tetap plot peserta ini ke ruangan?`
            );
            if (!confirmed) return;
        }
        try {
            await assignRoom.mutateAsync({
                conference_id: activeConferenceId,
                registration_id: registrationId,
                room_id: currentRoomId
            });
            toast.success("Partisipan berhasil di-plot ke ruangan ini");
        } catch (error) {
            toast.error("Gagal melakukan plot partisipan");
        }
    };

    const handleRemoveFromRoom = async (registrationId: number) => {
        try {
            await unassignRoom.mutateAsync({ registration_id: registrationId });
            toast.success("Plotting partisipan berhasil dihapus");
        } catch (error) {
            toast.error("Gagal menghapus plot partisipan");
        }
    };

    // Calculate Stats
    const stats = useMemo(() => {
        if (!participants) return { total: 0, plotted: 0, unplotted: 0, unpaid: 0 };
        const plotted = participants.filter(p => p.room_id !== null).length;
        const unpaid = participants.filter(p => !p.status || !PAID_STATUSES.includes(p.status)).length;
        return {
            total: participants.length,
            plotted: plotted,
            unplotted: participants.length - plotted,
            unpaid
        };
    }, [participants]);

    // Submission (ojs_submission_id) yang muncul lebih dari satu kali → indikasi duplikat registrasi (mis. re-invoice)
    const duplicateSubmissionIds = useMemo(() => {
        if (!participants) return new Set<string>();
        const counts = new Map<string, number>();
        participants.forEach(p => {
            if (p.ojs_submission_id === null || p.ojs_submission_id === undefined) return;
            const key = String(p.ojs_submission_id);
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        return new Set(Array.from(counts.entries()).filter(([, c]) => c > 1).map(([k]) => k));
    }, [participants]);

    const filteredParticipants = useMemo(() => {
        if (!participants) return [];
        return participants.filter(p => {
            if (filterRole === "presenter" && !p.is_presenter) return false;
            if (filterRole === "participant" && p.is_presenter) return false;

            if (filterPlotted === "plotted" && p.room_id === null) return false;
            if (filterPlotted === "unplotted" && p.room_id !== null) return false;
            if (filterPlotted === "plotted_this_room" && p.room_id !== currentRoomId) return false;
            if (filterPlotted === "plotted_other_room" && (p.room_id === null || p.room_id === currentRoomId)) return false;

            if (filterPayment === "paid" && (!p.status || !PAID_STATUSES.includes(p.status))) return false;
            if (filterPayment === "unpaid" && p.status && PAID_STATUSES.includes(p.status)) return false;

            if (filterSearch) {
                const s = filterSearch.toLowerCase();
                return (
                    p.participant_name?.toLowerCase().includes(s) ||
                    p.institution?.toLowerCase().includes(s) ||
                    p.submission_title?.toLowerCase().includes(s)
                );
            }
            return true;
        });
    }, [participants, filterSearch, filterRole, filterPlotted, filterPayment]);

    const paginated = filteredParticipants.slice((page - 1) * perPage, page * perPage);
    const totalPages = Math.ceil(filteredParticipants.length / perPage);

    if (!conferenceIdParam) {
        return <div className="p-8">Error: conference_id tidak ditemukan.</div>;
    }

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0 pt-6">
                        {/* Header Card */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <Users className="w-64 h-64" />
                </div>
                
                <div className="flex items-center gap-5 relative z-10">
                    <div className="hidden sm:flex p-4 bg-blue-50 dark:bg-blue-900/30 rounded-2xl border border-blue-100 dark:border-blue-800/50">
                        <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                            Kelola Peserta Ruangan
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-xl leading-relaxed flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-blue-500" />
                            {currentRoom ? currentRoom.name : "Loading..."}
                        </p>
                    </div>
                </div>

                <div className="relative z-10 flex items-center gap-3">
                                        <Button variant="outline" asChild className="rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 h-11 px-6 shadow-sm font-semibold transition-all">
                        <Link to="/admin/conference-rooms" className="flex items-center gap-2">
                            <ChevronLeft className="h-4 w-4" /> 
                            Kembali
                        </Link>
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 flex items-center gap-4 shadow-sm">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/40 rounded-xl text-blue-600 dark:text-blue-400">
                        <Users className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Total Partisipan</p>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.total}</h3>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 flex items-center gap-4 shadow-sm">
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/40 rounded-xl text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Sudah Diploting</p>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.plotted}</h3>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 flex items-center gap-4 shadow-sm">
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/40 rounded-xl text-amber-600 dark:text-amber-400">
                        <PieChart className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Belum Diploting</p>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.unplotted}</h3>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 flex items-center gap-4 shadow-sm">
                    <div className="p-3 bg-red-50 dark:bg-red-900/40 rounded-xl text-red-600 dark:text-red-400">
                        <PieChart className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Belum Lunas</p>
                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.unpaid}</h3>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                {/* Search & Filter */}
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 flex flex-col md:flex-row items-center gap-4">
                    <div className="w-full md:w-[250px]">
                        <Select value={filterConferenceId} onValueChange={(v) => { setFilterConferenceId(v); setPage(1); }}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl select-none">
                                <div className="truncate w-full text-left min-w-0">
                                    <SelectValue placeholder="Pilih Track Konferensi" />
                                </div>
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                <SelectItem value="all">Semua Track (Diabaikan)</SelectItem>
                                {conferences?.map((conf: any) => (
                                    <SelectItem key={conf.conference_id} value={String(conf.conference_id)} className="whitespace-normal break-words">
                                        {conf.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-full md:w-[200px]">
                        <Select value={filterRole} onValueChange={(v) => { setFilterRole(v); setPage(1); }}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl select-none">
                                <SelectValue placeholder="Semua Peran" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Peran</SelectItem>
                                <SelectItem value="presenter">Presenter</SelectItem>
                                <SelectItem value="participant">Participant Biasa</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-full md:w-[200px]">
                        <Select value={filterPlotted} onValueChange={(v) => { setFilterPlotted(v); setPage(1); }}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl select-none">
                                <SelectValue placeholder="Semua Status Plot" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Status Plot</SelectItem>
                                <SelectItem value="plotted">Sudah Diploting (Semua)</SelectItem>
                                <SelectItem value="plotted_this_room">Di Ruangan Ini</SelectItem>
                                <SelectItem value="plotted_other_room">Di Ruangan Lain</SelectItem>
                                <SelectItem value="unplotted">Belum Diploting</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-full md:w-[200px]">
                        <Select value={filterPayment} onValueChange={(v) => { setFilterPayment(v); setPage(1); }}>
                            <SelectTrigger className="w-full h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl select-none">
                                <SelectValue placeholder="Semua Status Bayar" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Status Bayar</SelectItem>
                                <SelectItem value="paid">Sudah Lunas</SelectItem>
                                <SelectItem value="unpaid">Belum Lunas</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="relative flex-1 w-full md:max-w-sm ml-auto">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari nama, instansi, judul paper..."
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(1); }}
                            className="pl-10 h-11 w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Partisipan</th>
                                <th className="px-6 py-4 font-semibold">Paper</th>
                                <th className="px-6 py-4 font-semibold text-center">Status Ruangan</th>
                                <th className="px-6 py-4 font-semibold text-right w-32 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading || isConferencesLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
                                        <p>Memuat data partisipan...</p>
                                    </td>
                                </tr>
                            ) : paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                                        <p className="text-lg font-medium text-slate-600 dark:text-slate-400">Data tidak ditemukan.</p>
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((p, idx) => {
                                    const isCurrentRoom = p.room_id === currentRoomId;
                                    const hasOtherRoom = p.room_id !== null && p.room_id !== currentRoomId;

                                    return (
                                        <tr key={p.registration_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                            <td className="px-6 py-4 text-center font-medium text-slate-500 dark:text-slate-400">
                                                {(page - 1) * perPage + idx + 1}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-900 dark:text-white mb-1">{p.participant_name}</div>
                                                <div className="text-sm text-slate-500 dark:text-slate-400">{p.institution}</div>
                                                <div className="mt-1.5 flex flex-wrap gap-1">
                                                    {p.is_presenter ? (
                                                        <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                                            Presenter
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                                                            Participant
                                                        </span>
                                                    )}
                                                    {p.status && PAID_STATUSES.includes(p.status) ? (
                                                        <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                                            Lunas
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                                            {p.status ? p.status.toUpperCase() : "BELUM LUNAS"}
                                                        </span>
                                                    )}
                                                    {p.ojs_submission_id !== null && p.ojs_submission_id !== undefined && duplicateSubmissionIds.has(String(p.ojs_submission_id)) && (
                                                        <span
                                                            className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                                            title="Ada lebih dari satu baris registrasi untuk submission paper ini (kemungkinan re-invoice). Pastikan memilih yang berstatus Lunas."
                                                        >
                                                            Duplikat Submission
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {p.submission_title ? (
                                                    <div className="max-w-[300px]">
                                                        <p className="font-medium text-sm text-slate-700 dark:text-slate-300 line-clamp-2" title={p.submission_title}>
                                                            {p.submission_title}
                                                        </p>
                                                        {p.submission_authors && (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1" title={p.submission_authors}>
                                                                Oleh: {p.submission_authors}
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">Tidak ada paper</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {isCurrentRoom ? (
                                                    <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                                        Di Ruangan Ini
                                                    </span>
                                                ) : hasOtherRoom ? (
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                                                            Ruangan Lain
                                                        </span>
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400">({p.room_name})</span>
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                                                        Belum Diplot
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">
                                                <div className="flex items-center justify-end gap-2">
                                                    {isCurrentRoom ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleRemoveFromRoom(p.registration_id)}
                                                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:border-red-900/50 dark:hover:bg-red-900/30 w-full justify-center"
                                                            disabled={unassignRoom.isPending}
                                                        >
                                                            Batal Plot
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="default"
                                                            size="sm"
                                                            onClick={() => handleAssign(p.registration_id, p.status)}
                                                            className="bg-blue-600 hover:bg-blue-700 text-white w-full justify-center"
                                                            disabled={assignRoom.isPending || hasOtherRoom}
                                                        >
                                                            Plot Ke Sini
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-slate-500 dark:text-slate-400">
                            Menampilkan <span className="font-semibold text-slate-900 dark:text-white">{(page - 1) * perPage + 1}</span> hingga <span className="font-semibold text-slate-900 dark:text-white">{Math.min(page * perPage, filteredParticipants.length)}</span> dari <span className="font-semibold text-slate-900 dark:text-white">{filteredParticipants.length}</span> partisipan
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Prev</span>
                            </Button>
                            <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                <span className="hidden sm:inline">Next</span>
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}





